import { useState, useEffect } from 'react';
import { Stack, Group, Text, Button, Card } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { NumberInput, Radio } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { ApiError } from '@repletoryreach/sdk-api';
import { useRequestWithdrawal } from '../../../data/useAffiliate';
import { formatMinor } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';

/**
 * Asking for money out.
 *
 * The two ways are put side by side because they behave differently and the
 * difference matters: a bank payment waits for a person to make it, while
 * credits land at once and can only be spent on the platform. The amount is
 * typed in rands and sent in minor units, as everywhere else.
 */
export function WithdrawDrawer({ opened, onClose, availableMinor, minimumMinor, hasAccount, accountLabel }: {
  opened: boolean;
  onClose: () => void;
  availableMinor: number;
  minimumMinor: number;
  hasAccount: boolean;
  accountLabel?: string;
}) {
  const t = useT();
  const request = useRequestWithdrawal();
  const [amount, setAmount] = useState<number | ''>('');
  const [method, setMethod] = useState<'eft' | 'wallet_credit'>(hasAccount ? 'eft' : 'wallet_credit');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setError(null);
    setAmount(availableMinor > 0 ? Math.round(availableMinor) / 100 : '');
    setMethod(hasAccount ? 'eft' : 'wallet_credit');
  }, [opened, availableMinor, hasAccount]);

  const minor = amount === '' ? 0 : Math.round(amount * 100);
  const tooSmall = minor > 0 && minor < minimumMinor;
  const tooMuch = minor > availableMinor;

  async function submit() {
    setError(null);
    try {
      await request.mutateAsync({ amount_minor: minor, method });
      notifySuccess(method === 'wallet_credit' ? t('refer.withdraw.converted') : t('refer.withdraw.requested'));
      onClose();
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: { message?: string } } | null) : null;
      setError(body?.error?.message ?? t('refer.withdraw.failed'));
    }
  }

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size="md" title={t('refer.withdraw.title')}>
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {t('refer.withdraw.available', { amount: formatMinor(availableMinor) })}
        </Text>

        {error && <Alert color="error" role="alert">{error}</Alert>}

        <NumberInput
          label={t('refer.withdraw.amount')}
          description={t('refer.withdraw.amountHint', { minimum: formatMinor(minimumMinor) })}
          value={amount}
          onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
          min={0}
          step={1}
          error={tooSmall ? t('refer.withdraw.belowMinimum', { minimum: formatMinor(minimumMinor) })
            : tooMuch ? t('refer.withdraw.tooMuch') : undefined}
        />

        <Stack gap="xs">
          <Text role="label">{t('refer.withdraw.method')}</Text>
          <Card withBorder>
            <Stack gap="sm">
              <Radio
                name="withdraw-method"
                label={t('refer.withdraw.eft')}
                checked={method === 'eft'}
                disabled={!hasAccount}
                onChange={() => setMethod('eft')}
              />
              <Text role="caption" c="var(--rr-color-text-muted)">
                {hasAccount
                  ? t('refer.withdraw.eftHint', { account: accountLabel ?? '' })
                  : t('refer.withdraw.eftNeedsAccount')}
              </Text>
              <Radio
                name="withdraw-method"
                label={t('refer.withdraw.credits')}
                checked={method === 'wallet_credit'}
                onChange={() => setMethod('wallet_credit')}
              />
              <Text role="caption" c="var(--rr-color-text-muted)">{t('refer.withdraw.creditsHint')}</Text>
            </Stack>
          </Card>
        </Stack>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
          <Button
            onClick={submit}
            loading={request.isPending}
            disabled={minor <= 0 || tooSmall || tooMuch || (method === 'eft' && !hasAccount)}
          >
            {method === 'wallet_credit' ? t('refer.withdraw.convertAction') : t('refer.withdraw.requestAction')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
